import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { TituloDTO, TipoDeCobrancaDTO } from '../../../../models/dashboard/titulo.model';
import { ContasPagarReceberConfig, SituacaoTitulo } from '../../contas-pagar-receber.types';
import { PaginatorState } from 'primeng/paginator';

@Component({
  selector: 'app-lista-titulos',
  templateUrl: './lista-titulos.component.html',
  styleUrls: ['./lista-titulos.component.scss'],
})
export class ListaTitulosComponent implements OnInit, OnDestroy {

  @Input() titulos: TituloDTO[] = [];
  @Input() loading = false;
  @Input() config!: ContasPagarReceberConfig;
  @Input() situacao: SituacaoTitulo = 'ABERTO';
  @Input() totalRecords = 0;
  @Input() first = 0;
  @Input() rows = 10;

  @Output() tituloClicado = new EventEmitter<TituloDTO>();
  @Output() pageChanged = new EventEmitter<PaginatorState>();

  expandedRows: { [key: number]: boolean } = {};

  labels = {
    nome_pessoa: '',
    vencimento: '',
    valor: '',
    saldo: '',
    aberto: '',
    pago: '',
    cancelado: '',
    parcela: '',
    pagamentos_titulo: '',
    nenhum_resultado: '',
  };

  private langChangeSub!: Subscription;

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  private loadTranslations(): void {
    this.labels = {
      nome_pessoa: this.translate.instant('contas-pagar-receber.labels.nome_pessoa'),
      vencimento: this.translate.instant('contas-pagar-receber.labels.vencimento'),
      valor: this.translate.instant('contas-pagar-receber.labels.valor_titulo'),
      saldo: this.translate.instant('contas-pagar-receber.labels.saldo'),
      aberto: this.translate.instant('contas-pagar-receber.labels.aberto'),
      pago: this.translate.instant('contas-pagar-receber.labels.pago'),
      cancelado: this.translate.instant('contas-pagar-receber.labels.cancelado'),
      parcela: this.translate.instant('contas-pagar-receber.labels.parcela'),
      pagamentos_titulo: this.translate.instant('contas-pagar-receber.labels.pagamentos_titulo'),
      nenhum_resultado: this.translate.instant('contas-pagar-receber.labels.nenhum_resultado'),
    };
  }

  onTituloClick(titulo: TituloDTO): void {
    this.tituloClicado.emit(titulo);
  }

  onPageChange(event: PaginatorState): void {
    this.pageChanged.emit(event);
  }

  toggleExpand(titulo: TituloDTO): void {
    if (this.expandedRows[titulo.id]) {
      delete this.expandedRows[titulo.id];
    } else {
      this.expandedRows[titulo.id] = true;
    }
  }

  temPagamentos(titulo: TituloDTO): boolean {
    return !!titulo.tipoDeCobranca && titulo.tipoDeCobranca.length > 0;
  }

  /** Extrai/normaliza a situacao (pode vir como string ou objeto { enum }) */
  getSituacao(titulo: TituloDTO): string {
    const sit: any = titulo.situacaoDeCobranca;
    if (!sit) return 'ABERTO';
    if (typeof sit === 'object' && sit?.enum) return sit.enum.toString().toUpperCase();
    return sit.toString().toUpperCase();
  }

  calcularTotalPago(titulo: TituloDTO): number {
    if (!titulo.tipoDeCobranca || titulo.tipoDeCobranca.length === 0) return 0;
    return titulo.tipoDeCobranca.reduce((sum, tc) => sum + (tc.valorMovimento || tc.valor), 0);
  }

  getMoedaSimbolo(moeda: any): string {
    const moedaStr = (typeof moeda === 'object' && moeda?.enum ? moeda.enum : moeda || '').toString().toUpperCase();
    switch (moedaStr) {
      case 'GUARANI': return 'Gs';
      case 'REAL': return 'R$';
      case 'DOLAR': return '$';
      case 'PESO': return '$';
      default: return '';
    }
  }

  /**
   * Formata um valor monetario de acordo com a moeda informada.
   * Replica o padrao de formatarValorMonetario da tela de Movimentacao.
   */
  formatarValor(valor: number | null | undefined, moeda: any): string {
    if (valor == null || isNaN(valor as number)) return '-';
    const moedaStr = (typeof moeda === 'object' && moeda?.enum ? moeda.enum : moeda || '').toString().toUpperCase();
    const configuracoes: Record<string, { locale: string; simbolo: string; decimais: boolean }> = {
      GUARANI: { locale: 'es-PY', simbolo: 'Gs', decimais: false },
      REAL: { locale: 'pt-BR', simbolo: 'R$', decimais: true },
      DOLAR: { locale: 'en-US', simbolo: '$', decimais: true },
      PESO: { locale: 'es-AR', simbolo: '$', decimais: true },
    };
    const config = configuracoes[moedaStr] ?? configuracoes['GUARANI'];
    const options: Intl.NumberFormatOptions = config.decimais
      ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      : { minimumFractionDigits: 0, maximumFractionDigits: 0 };
    const valorFormatado = new Intl.NumberFormat(config.locale, options).format(Math.abs(valor as number));
    return `${config.simbolo} ${valorFormatado}`;
  }

  formatarVencimento(vencimento: string): string {
    if (!vencimento) return '-';
    try {
      const date = new Date(vencimento);
      if (isNaN(date.getTime())) return vencimento;
      const lang = this.translate.currentLang || 'pt-BR';
      const locale = lang === 'es' ? 'es-PY' : 'pt-BR';
      return date.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return vencimento;
    }
  }
}
